import { notification } from "antd";
import cuid from "cuid";
import { filter, get, isNil, last, sortBy } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import { GENERATE_QUOTE_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { CREATE_LINK } from "utils/api/graphql/mutations/link";
import {
  GENERATE_EXTERNAL_QUOTE,
  SEND_B2C_QUOTE,
} from "utils/api/graphql/mutations/quotes";
import { CREATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { OFFERS } from "utils/api/graphql/queries/offers";
import { JOB_LIST, KEYS } from "utils/constants";
import { computeUpsellPricing } from "utils/helpers/lumineisUpsells";
import { filterByConditions } from "../../../..";
import { getMainOfferQueryVariables } from "../utils/offerUpsells";
import useInsurancePlans from "../../../Manual/Miltis/Comparison/Widgets/Upsells/hooks/useInsurancePlans";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

const Upsells = ({ processId, project, onNext, onBack, tracer }) => {
  const selectedProductId = get(project, "fields.lastSelected.0");
  const [addSubscription] = useMutation(CREATE_SUBSCRIPTION);
  const [generateQuoteDocuments] = useMutation(GENERATE_QUOTE_DOCUMENTS);
  const [generateExternalQuote] = useMutation(GENERATE_EXTERNAL_QUOTE);
  const [sendQuoteMutation] = useMutation(SEND_B2C_QUOTE);
  const [addLink] = useMutation(CREATE_LINK);
  const { t: tErrors } = useTranslation(NAME_SPACES.ERRORS);
  const API_ERRORS = tErrors("API", { returnObjects: true });

  const [selectedModules, setSelectedModules] = useState(
    get(project, "fields.upsells", []),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [getOffers, { loading: offersLoading }] = useLazyQuery(OFFERS);
  const { selectedInsurancePlans, fetchMainOffers, loading } =
    useInsurancePlans({
      insurancePlan: { id: selectedProductId },
      getOffers,
      project,
      setSelectedModules,
    });
  useQuery(OFFERS, {
    fetchPolicy: "no-cache",
    skip: !selectedProductId,
    variables: getMainOfferQueryVariables(selectedProductId),
    onCompleted: (offers) => {
      fetchMainOffers(offers);
    },
  });

  const handleModule = ({ module }) => {
    const id = get(module, "insurancePlan.id");
    setSelectedModules((prev) => {
      const exist = get(prev, selectedProductId)?.find(
        ({ insurancePlan }) => insurancePlan.id === id,
      );
      return exist
        ? {
            ...prev,
            [selectedProductId]: get(prev, selectedProductId, []).filter(
              ({ insurancePlan }) => insurancePlan.id !== id,
            ),
          }
        : {
            ...prev,
            [selectedProductId]: [...get(prev, selectedProductId, []), module],
          };
    });
  };

  const onSubmit = async ({ ignore = false } = {}) => {
    setIsSubmitting(true);
    try {
      // Persist the combination-adjusted price for each selected pack so the saved
      // quote reflects the Alptis combination discount rather than standalone sums.
      const currentModules = get(selectedModules, selectedProductId, []);
      const { costForModule } = computeUpsellPricing({
        modules: selectedInsurancePlans,
        selectedModules: currentModules,
      });
      const adjustedSelectedModules = {
        ...selectedModules,
        [selectedProductId]: currentModules.map((module) => ({
          ...module,
          cost: costForModule(module),
        })),
      };

      const profession = get(project, "contact.profession");
      if (
        [
          JOB_LIST.HOSPITAL_CIVIL_SERVICE,
          JOB_LIST.HOSPITAL_PRACTITIONER,
        ].includes(profession) &&
        get(project, "fields.hasCds")
      ) {
        await onNext({
          payload: {
            fields: {
              ...project.fields,
              upsells: ignore ? undefined : adjustedSelectedModules,
            },
          },
        });
        return;
      }
      const insurancePlanId = selectedProductId;
      const insurancePlans = [
        ...get(project, "fields.insurancePlans", []).filter(
          (id) => id !== insurancePlanId,
        ),
        insurancePlanId,
      ];
      const status = get(
        last(
          sortBy(
            filter(
              project.statuses,
              ({ status }) => status.process.id === processId,
            ),
            "createdDate",
          ),
        ),
        "status",
      );
      const { possibleStatuses } = status;
      const [possibleStatus] = filterByConditions(
        sortBy(possibleStatuses, "order"),
        {
          ...project,
          status,
        },
      );

      await new Promise((resolve, reject) => {
        addSubscription({
          variables: {
            data: {
              id: cuid(),
              project: { id: project.id },
              insurancePlan: {
                id: insurancePlanId,
              },
            },
          },
          onCompleted: ({
            addSubscription: { id: subscriptionId, insurancePlan },
          }) => {
            Promise.resolve(
              onNext({
                payload: {
                  locked: true,
                  visible: true,
                  fields: {
                    ...project.fields,
                    insurancePlans: insurancePlans,
                    ...(!isNil(possibleStatus?.nextStatus) && {
                      nextQuoteStatus: possibleStatus.nextStatus,
                    }),
                    currentSubscription: subscriptionId,
                    upsells: ignore ? undefined : adjustedSelectedModules,
                  },
                },
                onCompleted: () => {
                  if (project.visible || !get(project, "fields.shouldBeHidden"))
                    generateQuoteDocuments({
                      variables: {
                        data: {
                          project: { id: project.id },
                          insurancePlans: [{ id: insurancePlanId }],
                        },
                      },
                      onCompleted: () => {
                        generateExternalQuote({
                          variables: {
                            data: {
                              project: { id: project.id },
                              insurancePlans: [{ id: insurancePlanId }],
                            },
                          },
                          onCompleted: () => {
                            const source = localStorage.getItem(KEYS.SOURCE);
                            addLink({
                              variables: {
                                data: {
                                  url: get(
                                    insurancePlan,
                                    "config.generatedUrl",
                                    `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
                                  ),
                                  project: { id: project.id },
                                  subscription: { id: subscriptionId },
                                  fields: {
                                    process: { id: processId },
                                    status: {
                                      id: get(possibleStatus, "nextStatus"),
                                    },
                                  },
                                },
                              },
                              onCompleted: ({ addLink: linkData }) => {
                                sendQuoteMutation({
                                  variables: {
                                    data: {
                                      link: linkData?.id,
                                      project: { id: project.id },
                                      insurancePlans: [{ id: insurancePlanId }],
                                    },
                                  },
                                  onCompleted: () =>
                                    notification.open({
                                      message: "Devis envoyé",
                                      duration: 5,
                                      icon: (
                                        <IoCheckmarkCircleOutline
                                          color={COLORS.C_SUCCESS}
                                        />
                                      ),
                                    }),
                                });
                              },
                            });
                          },
                          onError: () => {
                            notification.error({
                              message: API_ERRORS.GENERIC,
                            });
                          },
                        });
                      },
                    });
                },
              }),
            )
              .then(resolve)
              .catch(reject);
          },
          onError: reject,
        });
      });
    } catch {
      setIsSubmitting(false);
    }
  };

  if (offersLoading || loading) return <Loader />;

  return (
    <>
      <Navbar
        progress={60}
        project={project}
        tracer={tracer}
        showCart
        showProfile
        step={3}
        stepCount={5}
        title={"Je renforce ma couverture"}
      />
      <View
        contact={get(project, "contact", {})}
        onNext={onSubmit}
        onBack={onBack}
        isSubmitting={isSubmitting}
        upsells={selectedInsurancePlans}
        selectedModules={selectedModules}
        projectId={project?.id}
        handleModule={handleModule}
        insurancePlanId={selectedProductId}
      />
    </>
  );
};

export default Upsells;
